import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { createRouteClosure } from '../../services/routeClosuresService';
import { enqueue } from '../../services/offlineQueue';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import TechnicianOrderDetailView from './TechnicianOrderDetailView';
import MapComponent from '../MapComponent';
import { ordenarOrdenesPorDistancia, ordenarPorDistanciaDesde, formatearDistancia, calcularDistancia, OFICINA_COORDS } from '../../utils/distanceUtils';
import { getCached, setCached, clearCached, formatAge } from '../../utils/apiCache';
import { tieneFotosDelEquipo } from '../../utils/tecnicoValidacion';
import { capturePageview } from '../../lib/analytics';
import { erroresAristonCierreRuta, necesitaTipoIntervencion, TIPOS_INTERVENCION_ARISTON } from '../../utils/validacionAriston';

// ── Estados de documento ──────────────────────────────────────────────────────
const STATE_PENDING          = 651457;
const STATE_WARRANTY         = 1004686;
const STATE_COLLECTED        = 1016219;
const STATE_IN_ROUTE         = 1018827;
const STATE_PENDING_SUBSTITUTION = 1016221;
const STATE_REJECTED         = 651446;
const STATE_INSTALACION      = 1305009;
const STATE_PENDING_PAYMENT  = 1007173;
const STATE_ABSENT           = 1016220;
const STATE_PENDING_MATERIAL = 1016222;
const STATE_PENDING_PRESUPUESTO = 1040205;
const STATE_CLOSED           = 651428;
const STATE_PRELIQ_ARISTON   = 1041994;

// Estados que "vuelven a necesitar programarse" — al cerrar ruta con la
// orden en uno de estos y un comentario del técnico puesto, ese comentario
// se traslada a "private-comments" (persiste aunque se reasigne la orden) y
// "comments" se vacía, para que la próxima visita empiece en blanco. Pedido
// explícito del usuario — deja fuera "Rechazado" (no hay forma de ponerlo
// desde esta app) y "Pendiente de sustitución" (no se mencionó).
const ESTADOS_ARCHIVAN_COMENTARIO = new Set([
  STATE_PENDING, STATE_PENDING_MATERIAL, STATE_PENDING_PRESUPUESTO, STATE_ABSENT,
]);

// ── Pre-liquidación por marca (rate-id) ───────────────────────────────────────
const RATE_BRANDS = {
  14778: { name: 'Ariston',  preState: 1041994 },
  14585: { name: 'Nofer',    preState: 1069646 },
  14581: { name: 'Svan',     preState: 1069647 },
  15605: { name: 'Logman',   preState: 1070405 },
  14584: { name: 'Beretta',  preState: 1226452 },
};

const PAYMENT_CASH     = 53268;
const PAYMENT_WARRANTY = 92449;

const STATE_LABEL = {
  651457:  'Pendiente',
  1018827: 'En ruta',
  1016222: 'Pend. material',
  1016221: 'Pend. sustitución',
  1007173: 'Pend. pago',
  651428:  'Cerrado',
  1016219: 'Cobrado',
  1004686: 'Garantía',
  1016220: 'Ausente',
  651446:  'Rechazado',
  1040205: 'Pend. presupuesto',
  1305009: 'Instalación',
  1041994: 'Pre-liq. Ariston',
  1069646: 'Pre-liq. Nofer',
  1069647: 'Pre-liq. Svan',
  1070405: 'Pre-liq. Logman',
  1226452: 'Pre-liq. Beretta',
  1069430: 'PM',
};
const STATE_COLOR = {
  651457:  'bg-yellow-100 text-yellow-700',
  1018827: 'bg-blue-100 text-blue-700',
  1016222: 'bg-orange-100 text-orange-700',
  1016221: 'bg-purple-100 text-purple-700',
  1007173: 'bg-amber-100 text-amber-700',
  651428:  'bg-green-100 text-green-700',
  1016219: 'bg-green-100 text-green-700',
  1004686: 'bg-teal-100 text-teal-700',
  1016220: 'bg-gray-100 text-gray-600',
  651446:  'bg-red-100 text-red-700',
  1040205: 'bg-indigo-100 text-indigo-700',
  1305009: 'bg-cyan-100 text-cyan-700',
  1069430: 'bg-rose-100 text-rose-700',
};

// ── Validación de órdenes (lógica Android RouteValidator) ─────────────────────
// "Imágenes del activo" no tiene por qué venir SOLO de la galería del activo
// en StelOrder (asset-images, subida desde la app de StelOrder aparte) — las
// fotos que el propio técnico sube en Finalizar visita (campo `fotos` de la
// orden, ver FinalizarVisitaModal.js) son evidencia igual de válida. Antes
// solo se miraba assetHasImages: un técnico que hacía exactamente lo que la
// app le pedía (fotografiar el equipo aquí) podía llegar al cierre de ruta
// bloqueado igualmente, sin ninguna pista de que había que ir a otra app.
// tieneFotosDelEquipo ahora vive en utils/tecnicoValidacion.js (compartida
// con FinalizarVisitaModal.js/TechnicianOrderDetailView.js — ver ese archivo
// para el porqué).

// La firma capturada en la app (firmado:true, ver finalizarVisita.js) cuenta
// aunque StelOrder siga en signed:false — StelOrder a veces no aplica el
// signed:true que le subimos (AV26/3474, 2026-11-04) y el cierre se bloqueaba
// con "Cobrado sin firmar" con la firma ya hecha. Salvo si la orden volvió a
// quedar abierta (reagendada): ahí el firmado es de la visita anterior.
// Mismo criterio que netlify/functions/_firmaApp.js.
export function firmadaEnApp(order) {
  const state = order?.['document-state-id'];
  return order?.firmado === true && state !== STATE_IN_ROUTE && state !== STATE_PENDING;
}

export function validateOrders(orders) {
  const errors             = [];
  const warnings           = []; // no bloquean el cierre, solo informan
  const stateChanges       = [];
  const archivarComentarios = []; // ver ESTADOS_ARCHIVAN_COMENTARIO
  const tiposIntervencion  = []; // garantías Ariston sin línea de servicio delante — el modal pregunta el tipo
  let autoCash  = 0;
  let completed = 0;

  for (const order of orders) {
    const ref       = order['full-reference'] || `#${order.id}`;
    const state     = order['document-state-id'];
    const signed    = order.signed === true || firmadaEnApp(order);
    const comments  = (order.comments || '').trim();
    const lines     = order.lines || [];
    const paymentId = order['payment-option-id'];
    const rateId    = order['rate-id'];

    if (signed && state !== STATE_IN_ROUTE) completed++;

    if (state === STATE_COLLECTED && paymentId === PAYMENT_CASH) {
      autoCash += order['total-amount'] || 0;
    }

    switch (state) {
      case STATE_PENDING_MATERIAL: {
        if (!signed)               errors.push({ ref, msg: 'Pendiente de material sin firmar' });
        if (!comments)             warnings.push({ ref, msg: 'Pendiente de material sin observaciones' });
        if (!tieneFotosDelEquipo(order)) warnings.push({ ref, msg: 'Sin fotos del equipo (súbelas en Finalizar visita o desde StelOrder)' });
        // No bloquea (todavía no hay nada que facturar, por eso no es error) —
        // pero sin la referencia del material en algún sitio, almacén no sabe
        // qué pedir sin tener que llamar al técnico a preguntar. El sitio
        // normal es `materialFaltante` (Finalizar visita ya obliga a
        // rellenarlo) — `lines`/`lineas` son las líneas de FACTURACIÓN, que
        // en un "Falta material" normal (sin cobro) se quedan vacías aunque
        // todo esté bien hecho, así que antes este aviso saltaba siempre
        // (Rafa, 2026-10-30, a raíz de confundir esto con que el material no
        // se guardaba en StelOrder — ver finalizarVisita.js).
        const lineasMaterial  = order.lineas || [];
        const materialFaltante = Array.isArray(order.materialFaltante) ? order.materialFaltante : [];
        if (!lines.length && !lineasMaterial.length && !materialFaltante.length)
          warnings.push({ ref, msg: 'Pendiente de material sin referencia de qué falta — anótalo para que almacén sepa qué pedir' });
        break;
      }

      case STATE_ABSENT:
        if (!comments) errors.push({ ref, msg: 'Ausente sin observaciones' });
        break;

      case STATE_COLLECTED: {
        if (!signed) errors.push({ ref, msg: 'Cobrado sin firmar' });
        // Sin ninguna línea (ni catálogo StelOrder — order.lines — ni material
        // añadido en campo — order.lineas), oficina no tiene de dónde sacar el
        // importe a facturar en fase 3 y acaba metiendo un número a mano en
        // FacturarModal sin ninguna referencia real. Esto sí lo puede arreglar
        // el técnico ahora mismo (añadir la línea en Finalizar visita), por
        // eso bloquea en vez de solo avisar.
        const lineasPropias = order.lineas || [];
        if (!lines.length && !lineasPropias.length)
          errors.push({ ref, msg: 'Cobrado sin ninguna línea de servicio/material — oficina no podrá facturarlo' });
        // Estos tres, en cambio, son datos de la ficha del cliente en StelOrder
        // (o el importe que ya viene del propio cobro) — el técnico no siempre
        // puede arreglarlos en el momento, así que solo avisan, no bloquean.
        // El DNI/NIF solo hace falta para facturas por encima de 999€ — no
        // avisar de esto en cobros pequeños, donde nunca se pide.
        if (!order.dniCliente && Number(order['total-amount']) > 999)
          warnings.push({ ref, msg: 'Cobrado sin DNI/NIF del cliente — hará falta para la factura' });
        if (!order['account-path'])
          warnings.push({ ref, msg: 'Cobrado sin cliente de StelOrder vinculado — no se podrá facturar hasta que oficina lo revise' });
        if (!order['total-amount'])
          warnings.push({ ref, msg: 'Cobrado con importe 0 o sin definir' });
        break;
      }

      case STATE_PENDING_SUBSTITUTION:
        if (!signed) errors.push({ ref, msg: 'Pendiente de sustitución sin firmar' });
        // Obligatorias, no solo recomendables: sin foto del equipo no hay forma
        // de justificar la sustitución después. Vale la del activo en StelOrder
        // o las que el técnico suba aquí mismo en Finalizar visita.
        if (!tieneFotosDelEquipo(order))
          errors.push({ ref, msg: 'Pendiente de sustitución sin fotos del equipo — obligatorias (súbelas en Finalizar visita o desde StelOrder)' });
        break;

      case STATE_REJECTED:
        if (!comments) warnings.push({ ref, msg: 'Rechazado sin observaciones' });
        break;

      case STATE_PENDING_PAYMENT: {
        if (!signed)   errors.push({ ref, msg: 'Pendiente de pago sin firmar' });
        if (!comments) errors.push({ ref, msg: 'Pendiente de pago sin observaciones' });
        // No es una situación válida: si se va a facturar más tarde, tiene que
        // quedar anotado ahora qué se hizo — después ya no hay forma de saberlo.
        const lineasPendientePago = order.lineas || [];
        if (!lines.length && !lineasPendientePago.length)
          errors.push({ ref, msg: 'Pendiente de pago sin ninguna línea de servicio/material' });
        break;
      }

      case STATE_WARRANTY:
        if (signed) {
          if (paymentId !== PAYMENT_WARRANTY)
            errors.push({ ref, msg: 'Garantía sin forma de pago "Garantía"' });
          // Obligatorios, no solo recomendables: sin esto no se puede tramitar
          // la garantía con el fabricante. Vale la foto del activo en StelOrder
          // o las que el técnico suba aquí mismo en Finalizar visita.
          if (!tieneFotosDelEquipo(order))
            errors.push({ ref, msg: 'Garantía sin fotos del equipo — obligatorias (súbelas en Finalizar visita o desde StelOrder)' });
          if (!order.assetSerialNumber)
            errors.push({ ref, msg: 'Garantía sin número de serie del activo — obligatorio' });
          const brand    = RATE_BRANDS[rateId];
          const newState = brand ? brand.preState : RATE_BRANDS[14778].preState;
          // Va a Pre-liquidación Ariston → tiene que pasar ya la misma
          // pre-validación que hará oficina en Liquidaciones. Antes eso se
          // descubría días después, cuando el técnico ya no estaba delante
          // del equipo para mirar el nº de serie.
          if (newState === STATE_PRELIQ_ARISTON) {
            // La línea de servicio no se le pide que la corrija a mano: el
            // modal de cierre le pregunta el tipo de intervención y se añade
            // sola como primera línea (anadirLineaServicio.js). Mientras no
            // lo elija, bloquea.
            if (necesitaTipoIntervencion(order)) {
              tiposIntervencion.push({ orderId: order.id, ref });
              errors.push({ ref, msg: 'Ariston: elige el tipo de intervención (más abajo) — se añadirá como primera línea', tipoIntervencion: true });
            }
            for (const msg of erroresAristonCierreRuta(order, { omitirLineas: true }))
              errors.push({ ref, msg: `Ariston: ${msg}` });
          }
          stateChanges.push({
            orderId:    order.id,
            ref,
            fromLabel:  'Garantía',
            toLabel:    `Pre-liquidación ${brand?.name || 'Ariston'}`,
            newStateId: newState,
          });
        } else {
          stateChanges.push({
            orderId:    order.id,
            ref,
            fromLabel:  'Garantía (sin firmar)',
            toLabel:    'Pendiente',
            newStateId: STATE_PENDING,
          });
        }
        break;

      case STATE_PENDING_PRESUPUESTO: {
        if (!comments) errors.push({ ref, msg: 'Pendiente de presupuesto sin observaciones' });
        // No bloquea porque de momento es solo un presupuesto — pero la
        // mayoría de estas visitas acaban cobrándose, así que conviene dejar
        // ya anotado qué se hizo y tener la firma, para no tener que volver.
        const lineasPresupuesto = order.lineas || [];
        if (!lines.length && !lineasPresupuesto.length)
          warnings.push({ ref, msg: 'Pendiente de presupuesto sin líneas — la mayoría de estas visitas acaban cobrándose' });
        if (!signed)
          warnings.push({ ref, msg: 'Pendiente de presupuesto sin firmar' });
        break;
      }

      case STATE_IN_ROUTE:
        if (signed) {
          errors.push({ ref, msg: 'Estado incorrecto: En ruta + firmado' });
        } else {
          stateChanges.push({
            orderId:    order.id,
            ref,
            fromLabel:  'En ruta',
            toLabel:    'Pendiente',
            newStateId: STATE_PENDING,
          });
        }
        break;

      case STATE_INSTALACION: {
        if (signed) {
          // Pasa directamente a Pendiente de pago, que ya exige observaciones
          // y líneas — si no las tiene, hay que pedirlas AHORA (el técnico
          // sigue delante), no dejar que la orden se transforme en un
          // "Pendiente de pago" roto que bloqueará el próximo cierre en el
          // que aparezca, cuando ya nadie recuerde qué se hizo.
          if (!comments) errors.push({ ref, msg: 'Instalación sin observaciones — hace falta antes de pasar a pendiente de pago' });
          const lineasInstalacion = order.lineas || [];
          if (!lines.length && !lineasInstalacion.length)
            errors.push({ ref, msg: 'Instalación sin ninguna línea de servicio/material — hace falta antes de pasar a pendiente de pago' });
          stateChanges.push({
            orderId:    order.id,
            ref,
            fromLabel:  'Instalación',
            toLabel:    'Pendiente de pago',
            newStateId: STATE_PENDING_PAYMENT,
          });
        } else {
          warnings.push({ ref, msg: 'Instalación activa sin finalizar' });
        }
        break;
      }

      case STATE_PRELIQ_ARISTON:
        // Puesta en marcha (finalizarVisita.js la manda directa a este
        // estado) — mismas reglas Ariston, salvo la línea de servicio.
        for (const msg of erroresAristonCierreRuta(order, { puestaEnMarcha: true }))
          errors.push({ ref, msg: `Ariston: ${msg}` });
        break;

      case STATE_PENDING:
        // Orden asignada que se ha quedado todo el día sin tocar — no bloquea
        // (puede que de verdad no diera tiempo), pero antes no había ninguna
        // señal de esto ni para el técnico ni para dirección.
        warnings.push({ ref, msg: 'Orden pendiente sin atender' });
        break;

      default:
        break;
    }

    // Trasladar el comentario del técnico a "private-comments" antes de que
    // la orden se quede esperando reprogramación — así el histórico de
    // intentos queda visible para seguimiento, y "comments" vuelve a estar
    // en blanco para la próxima visita. Solo si de verdad hay algo que
    // trasladar (si comments está vacío, o la orden bloqueó el cierre por
    // falta de observaciones, aquí no hay nada que hacer).
    if (ESTADOS_ARCHIVAN_COMENTARIO.has(state) && comments) {
      archivarComentarios.push({ orderId: order.id, ref, comments });
    }
  }

  return { errors, warnings, stateChanges, archivarComentarios, tiposIntervencion, autoCash, completed };
}

// Completitud del día: las órdenes "Pendiente" (todavía sin iniciar por el
// técnico — ver botón "Iniciar visita" en TechnicianOrderDetailView.js) no
// cuentan ni para la completitud en pantalla ni para lo que se manda a
// createRouteClosure al cerrar ruta — antes penalizaban al técnico por algo
// que, hasta ahora, no podía tocar desde esta app. Se exporta como función
// pura para poder testearla igual que validateOrders(), sin montar el
// componente.
export function computeCompletitud(orders) {
  const actionable = orders.filter(o => o['document-state-id'] !== STATE_PENDING);
  const assignedCount  = actionable.length;
  // Completada = firmada y no en estado "En ruta" (consistente con validateOrders)
  const completedCount = actionable.filter(o =>
    o.signed === true && o['document-state-id'] !== STATE_IN_ROUTE
  ).length;
  const completionPct = assignedCount > 0 ? Math.round((completedCount / assignedCount) * 100) : 0;
  return { assignedCount, completedCount, completionPct };
}

// Mismo listado que MOTIVOS_VALIDOS en justificarRutaNoCerrada.js.
const MOTIVOS_JUSTIFICACION = [
  ['cliente_ausente',     'Cliente ausente'],
  ['incidencia_tecnica',  'Incidencia técnica'],
  ['emergencia_personal', 'Emergencia personal'],
  ['fallo_app',           'Fallo de la app'],
  ['otro',                'Otro'],
];

function todayString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const DONE_STATES = new Set([1016219, 1016220, 1007173, 1016222, 1040205, 1016221, 1041994, 1069646, 1069647, 1070405, 1226452, 651428]);

// Estados cuya validación depende de datos del activo (imágenes/nº serie) que
// vienen de StelOrder vía el mirror de Firebase (sync cada 30 min) — si el
// técnico sube la foto desde la app de StelOrder y vuelve aquí a cerrar ruta
// enseguida, ese mirror puede no haberse actualizado todavía. Por eso estos
// se refrescan en vivo justo al pulsar "Cerrar ruta" (ver handleOpenCloseModal).
const ASSET_DEPENDENT_STATES = new Set([STATE_PENDING_MATERIAL, STATE_PENDING_SUBSTITUTION, STATE_WARRANTY]);

function getOrderAssetId(order) {
  const first = order.assets?.[0];
  if (!first) return null;
  if (first.id) return Number(first.id);
  const pathId = Number(first.path?.split('/').pop());
  return isNaN(pathId) ? null : pathId;
}

// Modal de cierre de ruta — compartido entre el cierre de HOY y el de una
// ruta anterior pendiente (Mejora 3). Antes eran dos copias de JSX casi
// idénticas mantenidas a mano por separado, y eso ya causó una
// desincronización real: la de "ruta anterior" no mostraba los cambios de
// estado automáticos ni dejaba saltar a la orden desde un error/aviso.
function ModalCierreRuta({
  validation, totalOrdenes, titulo, labelEfectivo,
  confirmando, aplicandoEstados, onCancelar, onConfirmar,
  onBuscarOrden, onSeleccionarOrden,
  onElegirTipoIntervencion, guardandoTipo, errorTipo,
}) {
  const tipos = validation.tiposIntervencion || [];
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50">
      <div className="bg-white w-full rounded-t-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto" />
        <h3 className="font-bold text-gray-900 text-lg">{titulo}</h3>

        {/* Resumen firmadas */}
        <div className="bg-gray-50 rounded-xl p-3 text-sm text-gray-600 flex items-center justify-between">
          <span>
            <span className="font-semibold">{validation.completed}</span> de{' '}
            <span className="font-semibold">{totalOrdenes}</span> órdenes firmadas
          </span>
          {validation.completed === totalOrdenes && totalOrdenes > 0 && (
            <span className="text-green-600 font-semibold text-xs">¡Ruta perfecta!</span>
          )}
        </div>

        {/* Cambios de estado automáticos */}
        {validation.stateChanges.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">
              Cambios de estado automáticos
            </p>
            <div className="space-y-1.5">
              {validation.stateChanges.map((sc, i) => (
                <div key={i} className="flex items-center gap-2 bg-blue-50 rounded-xl px-3 py-2">
                  <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{sc.ref}</span>
                  <span className="text-xs text-gray-400">→</span>
                  <span className="text-xs text-blue-700 font-semibold truncate">{sc.fromLabel}</span>
                  <span className="text-xs text-gray-400">→</span>
                  <span className="text-xs text-blue-900 font-bold truncate">{sc.toLabel}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Errores bloqueantes */}
        {validation.errors.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold text-red-600 uppercase tracking-wider">
              ❌ Errores — bloquean el cierre ({validation.errors.length})
            </p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {validation.errors.map((e, i) => {
                const ordenRef = onBuscarOrden(e.ref);
                const Wrap = ordenRef ? 'button' : 'div';
                return (
                  <Wrap
                    key={i}
                    {...(ordenRef ? { type: 'button', onClick: () => onSeleccionarOrden(ordenRef) } : {})}
                    className={`w-full flex items-start gap-2 bg-red-50 border border-red-100 rounded-xl px-3 py-2 text-left ${ordenRef ? 'active:scale-[0.99] transition-transform' : ''}`}
                  >
                    <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{e.ref}</span>
                    <span className="text-xs text-red-700 flex-1">{e.msg}</span>
                    {ordenRef && <span className="text-xs text-red-400 flex-shrink-0">Abrir ›</span>}
                  </Wrap>
                );
              })}
            </div>
          </div>
        )}

        {/* Tipo de intervención Ariston — garantías sin la línea de servicio
            delante. Al elegirlo se añade como primera línea de la orden
            (anadirLineaServicio.js) y su error desaparece de la lista. */}
        {tipos.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold text-teal-700 uppercase tracking-wider">
              🛡️ ¿Qué intervención hiciste? ({tipos.length})
            </p>
            <p className="text-xs text-gray-500">
              Ariston necesita el tipo de intervención como primera línea de la orden. Elígelo y se añade solo.
            </p>
            {tipos.map(t => (
              <div key={t.orderId} className="bg-teal-50 border border-teal-100 rounded-xl p-3 space-y-2">
                <p className="text-xs font-semibold text-gray-700">{t.ref}</p>
                <div className="grid grid-cols-2 gap-2">
                  {TIPOS_INTERVENCION_ARISTON.map(op => (
                    <button
                      key={op.tipo}
                      type="button"
                      disabled={!!guardandoTipo}
                      onClick={() => onElegirTipoIntervencion(t.orderId, op.tipo)}
                      className="text-left bg-white border border-teal-200 hover:border-teal-500 rounded-xl px-3 py-2 disabled:opacity-50 transition-colors"
                    >
                      <span className="block text-xs font-bold text-teal-900">
                        {guardandoTipo === `${t.orderId}:${op.tipo}` ? 'Guardando…' : op.label}
                      </span>
                      <span className="block text-[11px] text-gray-500">{op.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {errorTipo && <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{errorTipo}</p>}
          </div>
        )}

        {/* Avisos no bloqueantes */}
        {validation.warnings?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-bold text-amber-600 uppercase tracking-wider">
              ⚠️ Avisos ({validation.warnings.length})
            </p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {validation.warnings.map((w, i) => {
                const ordenRef = onBuscarOrden(w.ref);
                const Wrap = ordenRef ? 'button' : 'div';
                return (
                  <Wrap
                    key={i}
                    {...(ordenRef ? { type: 'button', onClick: () => onSeleccionarOrden(ordenRef) } : {})}
                    className={`w-full flex items-start gap-2 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 text-left ${ordenRef ? 'active:scale-[0.99] transition-transform' : ''}`}
                  >
                    <span className="text-xs font-semibold text-gray-700 flex-shrink-0">{w.ref}</span>
                    <span className="text-xs text-amber-700 flex-1">{w.msg}</span>
                    {ordenRef && <span className="text-xs text-amber-500 flex-shrink-0">Abrir ›</span>}
                  </Wrap>
                );
              })}
            </div>
          </div>
        )}

        {/* Efectivo */}
        <div className="bg-green-50 border border-green-100 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-green-800 uppercase tracking-wider">{labelEfectivo}</p>
            <p className="text-xs text-green-600 mt-0.5">Calculado de las órdenes cobradas</p>
          </div>
          <p className="text-2xl font-bold text-green-700">{(validation.autoCash).toFixed(2)} €</p>
        </div>

        {validation.errors.length > 0 && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-center font-semibold">
            Corrige los errores antes de cerrar la ruta
          </p>
        )}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onCancelar}
            className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 font-semibold rounded-xl transition-colors text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirmar}
            disabled={confirmando || validation.errors.length > 0}
            className="flex-1 flex items-center justify-center gap-2 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {confirmando && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            {aplicandoEstados ? 'Actualizando estados...' : confirmando ? 'Guardando...' : 'Confirmar cierre'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TechnicianOrdersView({ refreshTrigger } = {}) {
  const { user } = useAuth();
  const [orders, setOrders]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [successToast, setSuccessToast]   = useState('');
  const [error, setError]                 = useState('');
  const [lastUpdate, setLastUpdate]       = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [validation, setValidation]       = useState(null);
  const [closing, setClosing]             = useState(false);
  const [closedResult, setClosedResult]   = useState(null);
  const [existingClosure, setExistingClosure] = useState(null);
  const [applyingStates, setApplyingStates] = useState(false);
  const [refreshingAssets, setRefreshingAssets] = useState(false);
  const [showRouteMap, setShowRouteMap]   = useState(false);
  const [userLocation, setUserLocation]   = useState(null);
  const geoWatchRef = useRef(null);
  const today = todayString();

  // ── Mejora 3: bloqueo por ruta anterior sin cerrar. Activo por defecto
  // desde 2026-10-26 (antes había que encenderlo); config/operaciones.
  // exigirCierreRutaAnterior = false lo apaga, como interruptor de emergencia ─
  const [rutaAnteriorPendiente, setRutaAnteriorPendiente] = useState(null); // { fecha } | null
  const [checkingRutaAnterior, setCheckingRutaAnterior]   = useState(true);
  const [blockingOrders, setBlockingOrders]         = useState([]);
  const [blockingValidation, setBlockingValidation] = useState(null);
  const [showBlockingCloseModal, setShowBlockingCloseModal] = useState(false);
  const [loadingBlockingOrders, setLoadingBlockingOrders]   = useState(false);
  const [closingBlocking, setClosingBlocking]       = useState(false);
  const [showJustificarModal, setShowJustificarModal] = useState(false);
  const [justificarMotivo, setJustificarMotivo]     = useState('');
  const [justificarDetalle, setJustificarDetalle]   = useState('');
  const [enviandoJustificacion, setEnviandoJustificacion] = useState(false);

  // Única sub-navegación real dentro de la pantalla de Órdenes del técnico:
  // lista ↔ detalle de una orden (selectedOrder). Las demás pantallas con
  // pestañas propias quedan fuera de este cambio (Rafa, 2026-11-03).
  useEffect(() => {
    capturePageview(selectedOrder ? 'tecnico:ordenes:detalle' : 'tecnico:ordenes:lista');
  }, [!!selectedOrder]); // eslint-disable-line

  // Activa GPS cuando el mapa de ruta está abierto
  useEffect(() => {
    if (!showRouteMap || !navigator.geolocation) return;
    geoWatchRef.current = navigator.geolocation.watchPosition(
      pos => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      err => console.warn('Geolocalización no disponible:', err.message),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    return () => {
      if (geoWatchRef.current != null) {
        navigator.geolocation.clearWatch(geoWatchRef.current);
        geoWatchRef.current = null;
      }
    };
  }, [showRouteMap]);

  const loadOrders = useCallback(async (forceRefresh = false) => {
    if (!user?.employeeId) return;
    setLoading(true);
    setError('');

    const cacheKey = `techOrders_${user.employeeId}_${today}`;

    // Usar caché si existe y no se pide refresco
    if (!forceRefresh) {
      const cached = getCached(cacheKey);
      if (cached) {
        setOrders(cached.data.workorders || []);
        setLastUpdate(cached.ts);
        setLoading(false);
        return;
      }
    }

    try {
      const url = forceRefresh
        ? `/.netlify/functions/getTechnicianOrders?employeeId=${user.employeeId}&date=${today}&refresh=1`
        : `/.netlify/functions/getTechnicianOrders?employeeId=${user.employeeId}&date=${today}`;
      const fetchOpts = { ...(forceRefresh ? { cache: 'reload' } : {}), headers: authService.authHeader() };
      const resp = await fetch(url, fetchOpts);
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);
      setOrders(data.workorders || []);
      setCached(cacheKey, data);
      setLastUpdate(Date.now());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user?.employeeId, today]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Disparado por el botón "Sincronizar con StelOrder ahora" de TechnicianApp.js
  // (solo admin probando como técnico) tras bajar los cambios de StelOrder —
  // sin esto, la lista se quedaba con la caché vieja hasta el siguiente
  // refresco manual aunque el sync ya hubiera traído la orden nueva.
  const isFirstRefreshTrigger = useRef(true);
  useEffect(() => {
    if (isFirstRefreshTrigger.current) { isFirstRefreshTrigger.current = false; return; }
    loadOrders(true);
  }, [refreshTrigger, loadOrders]);

  useEffect(() => {
    if (!user?.employeeId) return;
    getDocs(query(
      collection(db, 'route_closures'),
      where('employeeId', '==', user.employeeId),
      where('dateString', '==', today)
    )).then(snap => {
      if (!snap.empty) setExistingClosure(snap.docs[0].data());
    }).catch(() => {});
  }, [user?.employeeId, today]);

  // Mejora 3: se comprueba siempre, salvo que oficina haya apagado el
  // interruptor (exigirCierreRutaAnterior === false) en Cierres de ruta.
  useEffect(() => {
    if (!user?.employeeId) { setCheckingRutaAnterior(false); return; }
    let cancelado = false;
    (async () => {
      try {
        const cfgSnap = await getDoc(doc(db, 'config', 'operaciones'));
        const activo = cfgSnap.data()?.exigirCierreRutaAnterior !== false;
        if (!activo) { if (!cancelado) setCheckingRutaAnterior(false); return; }
        const resp = await fetch(`/.netlify/functions/getRutaAnteriorPendiente?nombre=${encodeURIComponent(user.name || '')}`, {
          headers: authService.authHeader(),
        });
        const data = await resp.json();
        if (!cancelado && data.success && data.pendiente) {
          setRutaAnteriorPendiente({ fecha: data.fecha });
        }
      } catch {
        // Si falla la comprobación, no se bloquea al técnico — mejor dejar
        // trabajar que romper la pantalla de Órdenes por un fallo de red.
      } finally {
        if (!cancelado) setCheckingRutaAnterior(false);
      }
    })();
    return () => { cancelado = true; };
  }, [user?.employeeId, user?.name]);

  // Carga ruta confirmada del día desde Firestore rutas_planificadas/{today}_{nombre}
  useEffect(() => {
    if (!user?.name) return;
    const docId = `${today}_${user.name}`;
    getDocs(query(collection(db, 'rutas_planificadas'), where('fecha', '==', today), where('tecnico', '==', user.name)))
      .then(snap => {
        if (!snap.empty) setRutaConfirmada(snap.docs[0].data());
      })
      .catch(() => {});
  }, [user?.name, today]);

  // Encuentra la orden de la lista que corresponde a un error/aviso del
  // modal de cierre (identificados por referencia) — para poder saltar
  // directo a corregirla en vez de que el técnico tenga que buscarla.
  const findOrderByRef = (ref) => orders.find(o => (o['full-reference'] || `#${o.id}`) === ref) || null;

  const { assignedCount, completedCount, completionPct } = computeCompletitud(orders);

  // ¿Esta orden, con los datos que tenemos AHORA MISMO, va a bloquear o avisar
  // por falta de imágenes/nº de serie del activo? Solo estas se refrescan en
  // vivo contra StelOrder — así, si el técnico no tiene ningún problema de
  // este tipo (el caso normal, la mayoría de los días), no se gasta ni una
  // llamada extra a la API de StelOrder, que ya va justa de margen por los
  // crons de sincronización cada 30 min.
  const needsLiveAssetCheck = (order) => {
    const state = order['document-state-id'];
    if (!ASSET_DEPENDENT_STATES.has(state)) return false;
    // Si ya hay fotos propias de esta visita, no hace falta ir a comprobar en
    // vivo si StelOrder tiene imágenes del activo — ya hay evidencia.
    if (!tieneFotosDelEquipo(order)) return true;
    if (state === STATE_WARRANTY && order.signed && !order.assetSerialNumber) return true;
    return false;
  };

  // Garantía firmada (va a Pre-liquidación Ariston) o puesta en marcha que
  // ahora mismo falla alguna regla Ariston del activo — puede que el técnico
  // lo acabe de corregir en "Editar activo" y el dato que tenemos sea viejo.
  const needsLiveAristonCheck = (order) => {
    const state = order['document-state-id'];
    if (state === STATE_PRELIQ_ARISTON) return erroresAristonCierreRuta(order, { puestaEnMarcha: true }).length > 0;
    if (state === STATE_WARRANTY && order.signed) return erroresAristonCierreRuta(order).length > 0;
    return false;
  };

  // Refresca en vivo desde StelOrder lo justo para validar el cierre: el
  // estado de todas las órdenes, y el activo solo de las que están fallando
  // alguna comprobación que depende de él. Compartido entre el cierre de hoy
  // y el de la ruta anterior pendiente — antes este último validaba con lo
  // que hubiera en Firebase, sin refrescar nada.
  const refrescarEnVivo = async (lista) => {
    let freshOrders = lista;
    try {
      // 1) Estado de las propias órdenes, en vivo desde StelOrder — siempre,
      // no solo cuando algo parece fallar. Lo que escribe el técnico desde
      // "Finalizar visita" ya llega fresco a Firebase de inmediato, pero si
      // alguien tocó el estado de una orden directamente en StelOrder
      // (oficina corrigiendo algo, o el técnico usando la app de StelOrder
      // en vez de la nuestra), Firebase puede seguir mostrando el estado
      // viejo hasta el próximo cron (cada 30 min) — y cerrar la ruta con ese
      // dato desactualizado puede bloquear un cierre bueno, o dejar pasar
      // uno que no lo está. Es 1 sola llamada extra, una vez al día.
      const workorderIds = lista.map(o => o.id).filter(Boolean);
      if (workorderIds.length) {
        const resp = await fetch('/.netlify/functions/getWorkordersLive', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body:    JSON.stringify({ workorderIds }),
        });
        const data = await resp.json();
        if (data.success && data.orders) {
          freshOrders = lista.map(o => {
            const fresh = data.orders[o.id];
            if (!fresh) return o;
            // Líneas todavía en cola hacia StelOrder (Finalizar visita, o la
            // línea de servicio elegida en este cierre): las nuestras son las
            // buenas — StelOrder aún devuelve las de antes de subirlas.
            const merged = o.stelSyncEstado === 'pendiente' ? { ...o, ...fresh, lines: o.lines } : { ...o, ...fresh };
            // El signed:false de StelOrder no borra la firma hecha en la app (ver firmadaEnApp).
            return firmadaEnApp(merged) ? { ...merged, signed: true } : merged;
          });
        }
      }

      // 2) Imágenes/nº de serie del activo, en vivo — solo para las órdenes
      // que, con el estado ya actualizado del paso 1, están fallando esa
      // comprobación ahora mismo (mismo criterio que antes).
      const staleOrders = freshOrders.filter(o => needsLiveAssetCheck(o) || needsLiveAristonCheck(o));
      if (staleOrders.length) {
        const assetIds = [...new Set(staleOrders.map(getOrderAssetId).filter(Boolean))];
        if (assetIds.length) {
          const respAssets = await fetch('/.netlify/functions/getAssetsLive', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
            body:    JSON.stringify({ assetIds }),
          });
          const dataAssets = await respAssets.json();
          if (dataAssets.success && dataAssets.assets) {
            freshOrders = freshOrders.map(o => {
              const assetId = getOrderAssetId(o);
              const fresh   = assetId ? dataAssets.assets[assetId] : null;
              // Mismo fallback que getTechnicianOrders.js: si el activo sigue
              // sin serie, vale la que el técnico capturó en la visita.
              return fresh
                ? { ...o, ...fresh, assetSerialNumber: fresh.assetSerialNumber || o.numeroSerieCapturado || '' }
                : o;
            });
          }
        }
      }
    } catch (e) {
      // Si falla el refresco en vivo seguimos con lo que ya teníamos en
      // memoria — mejor validar con datos algo desactualizados que dejar al
      // técnico sin poder abrir el modal de cierre.
    }
    return freshOrders;
  };

  // Tipo de intervención Ariston elegido en el modal de cierre → se añade
  // como primera línea de la orden y se revalida en el acto, para que su
  // error desaparezca sin tener que cerrar y volver a abrir el modal.
  // `anterior`: es el cierre de la ruta anterior pendiente (blockingOrders).
  const [guardandoTipo, setGuardandoTipo] = useState(null); // `${orderId}:${tipo}`
  const [errorTipo, setErrorTipo]         = useState('');
  const elegirTipoIntervencion = async (orderId, tipo, { anterior = false } = {}) => {
    setGuardandoTipo(`${orderId}:${tipo}`);
    setErrorTipo('');
    try {
      const resp = await fetch('/.netlify/functions/anadirLineaServicio', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body:    JSON.stringify({ workorderId: orderId, tipo }),
      });
      const d = await resp.json();
      if (!d.success) throw new Error(d.error || 'No se pudo guardar el tipo de intervención');
      const aplicar = (lista) => lista.map(o =>
        String(o.id) === String(orderId) ? { ...o, lines: d.lines, stelSyncEstado: 'pendiente' } : o
      );
      if (anterior) {
        const nuevas = aplicar(blockingOrders);
        setBlockingOrders(nuevas);
        setBlockingValidation(validateOrders(nuevas));
      } else {
        const nuevas = aplicar(orders);
        setOrders(nuevas);
        setValidation(validateOrders(nuevas));
      }
    } catch (e) {
      setErrorTipo(e instanceof TypeError ? 'Sin conexión — inténtalo cuando tengas cobertura' : e.message);
    } finally {
      setGuardandoTipo(null);
    }
  };

  const handleOpenCloseModal = async () => {
    setRefreshingAssets(true);
    const freshOrders = await refrescarEnVivo(orders);
    setOrders(freshOrders);
    setRefreshingAssets(false);
    const result = validateOrders(freshOrders);
    setValidation(result);
    setShowCloseModal(true);
  };

  // Aplica los cambios de estado automáticos y el traslado de comentarios de
  // una validación, y crea el cierre de ruta — compartido entre el cierre de
  // HOY (handleCloseRoute) y el de una ruta anterior pendiente
  // (handleConfirmBlockingClose). Antes eran dos copias independientes: la
  // de "ruta anterior" ni siquiera aplicaba los cambios de estado
  // automáticos (p. ej. garantía → pre-liquidación) ni el traslado de
  // comentarios — confirmar el cierre de una ruta vieja podía dejar esas
  // órdenes en un estado que ya no correspondía, sin ningún aviso.
  const aplicarCambiosYCerrar = async (val, { assigned, targetDateString } = {}) => {
    // Aplicar cambios de estado automáticos — si alguno falla (StelOrder
    // caído, límite de peticiones...) antes esto se ignoraba en silencio:
    // la ruta se cerraba igual como si todo hubiera ido bien y nadie se
    // enteraba de que la orden se quedó en su estado anterior. Ahora los
    // fallos se anotan como aviso en el propio cierre.
    let stateChangeFailures = [];
    if (val?.stateChanges?.length) {
      setApplyingStates(true);
      const results = await Promise.allSettled(
        val.stateChanges.map(sc =>
          fetch('/.netlify/functions/updateWorkorderState', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
            body:    JSON.stringify({ workorderId: sc.orderId, newStateId: sc.newStateId }),
          }).then(r => ({ ok: r.ok, sc }))
        )
      );
      stateChangeFailures = results
        .map(r => (r.status === 'rejected' || !r.value?.ok) ? (r.value?.sc || null) : null)
        .filter(Boolean);
      setApplyingStates(false);
    }

    // Trasladar a "private-comments" el comentario de las órdenes que se
    // quedan pendientes de reprogramar (pendiente/material/presupuesto/
    // ausente) — mismo criterio de "no bloquear, solo avisar si falla" que
    // los cambios de estado de arriba.
    let archivarFailures = [];
    if (val?.archivarComentarios?.length) {
      const results = await Promise.allSettled(
        val.archivarComentarios.map(ac =>
          fetch('/.netlify/functions/archivarComentarioVisita', {
            method:  'POST',
            headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
            body:    JSON.stringify({ workorderId: ac.orderId, tecnicoNombre: user.name }),
          }).then(r => ({ ok: r.ok, ac }))
        )
      );
      archivarFailures = results
        .map(r => (r.status === 'rejected' || !r.value?.ok) ? (r.value?.ac || null) : null)
        .filter(Boolean);
    }

    const efectivo        = val?.autoCash || 0;
    const signedCompleted = val?.completed ?? 0;
    const warnings = [
      ...(val?.warnings || []),
      ...stateChangeFailures.map(sc => ({
        ref: sc.ref,
        msg: `No se pudo aplicar el cambio automático de estado (${sc.fromLabel} → ${sc.toLabel}) — revisar y cambiarlo a mano en StelOrder`,
      })),
      ...archivarFailures.map(ac => ({
        ref: ac.ref,
        msg: 'No se pudo trasladar el comentario a "private-comments" — revisar a mano en StelOrder',
      })),
    ];

    // createRouteClosure ahora comprueba el duplicado de forma atómica en
    // el servidor (ID determinista + precondición "no debe existir"), así
    // que un doble toque en "Confirmar cierre" no puede crear dos cierres.
    const result = await createRouteClosure({
      employeeName: user.name,
      assigned,
      completed:    signedCompleted,
      efectivo,
      cash:         efectivo > 0,
      // Avisos de validateOrders() (DNI/cliente StelOrder/importe faltante en
      // órdenes cobradas, órdenes pendientes sin atender...) — antes se
      // calculaban y se enseñaban un segundo en el modal del técnico y se
      // perdían; ahora quedan en el cierre para que dirección los vea.
      warnings,
      ...(targetDateString ? { targetDateString } : {}),
    });

    return { result, efectivo, signedCompleted };
  };

  const handleCloseRoute = async () => {
    setClosing(true);
    setError('');
    try {
      const { result, efectivo, signedCompleted } = await aplicarCambiosYCerrar(validation, { assigned: assignedCount });

      if (result.alreadyClosed) {
        const c = result.closure;
        setClosedResult({
          completedCount: c.completed || 0,
          assignedCount:  c.assigned  || 0,
          completionPct:  c.assigned > 0 ? Math.round(((c.completed || 0) / c.assigned) * 100) : 0,
          efectivo:       c.efectivo  || 0,
        });
      } else {
        setClosedResult({ completedCount: signedCompleted, assignedCount, completionPct, efectivo, queued: !!result.queued });
      }
      setShowCloseModal(false);
      if (result.queued) {
        alert('Ruta cerrada sin conexión. Se enviará automáticamente en cuanto vuelvas a tener cobertura.');
      }
    } catch (e) {
      setError(e.message || 'Error al cerrar la ruta');
    } finally {
      setClosing(false);
      setApplyingStates(false);
    }
  };

  // ── Mejora 3: cerrar (o justificar) la ruta anterior pendiente ────────────
  // Flujo aparte del cierre de HOY (handleOpenCloseModal/handleCloseRoute) a
  // propósito: no toca ni comparte estado con esas validaciones existentes.
  const handleOpenBlockingCloseModal = async () => {
    if (!rutaAnteriorPendiente) return;
    setLoadingBlockingOrders(true);
    try {
      const resp = await fetch(
        `/.netlify/functions/getTechnicianOrders?employeeId=${user.employeeId}&date=${rutaAnteriorPendiente.fecha}`,
        { headers: authService.authHeader() }
      );
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);
      const ordersDia = await refrescarEnVivo(data.workorders || []);
      setBlockingOrders(ordersDia);
      setBlockingValidation(validateOrders(ordersDia));
      setShowBlockingCloseModal(true);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las órdenes de ese día');
    } finally {
      setLoadingBlockingOrders(false);
    }
  };

  const handleConfirmBlockingClose = async () => {
    if (!blockingValidation || !rutaAnteriorPendiente) return;
    setClosingBlocking(true);
    try {
      const { result } = await aplicarCambiosYCerrar(blockingValidation, {
        assigned: blockingOrders.length,
        targetDateString: rutaAnteriorPendiente.fecha,
      });
      setShowBlockingCloseModal(false);
      setRutaAnteriorPendiente(null);
      if (result.queued) {
        alert('Ruta cerrada sin conexión. Se enviará automáticamente en cuanto vuelvas a tener cobertura.');
      }
    } catch (e) {
      setError(e.message || 'Error al cerrar la ruta anterior');
    } finally {
      setClosingBlocking(false);
    }
  };

  const handleEnviarJustificacion = async () => {
    if (!rutaAnteriorPendiente || !justificarMotivo || !justificarDetalle.trim()) return;
    setEnviandoJustificacion(true);
    const payload = {
      fecha:   rutaAnteriorPendiente.fecha,
      motivo:  justificarMotivo,
      detalle: justificarDetalle.trim(),
      employeeName: user.name,
    };
    const url = '/.netlify/functions/justificarRutaNoCerrada';
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        enqueue({ kind: 'justificarRuta', url, body: payload });
      } else {
        const resp = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
          body: JSON.stringify(payload),
        });
        const data = await resp.json();
        if (!data.success) throw new Error(data.error);
      }
      setShowJustificarModal(false);
      setRutaAnteriorPendiente(null);
      setJustificarMotivo('');
      setJustificarDetalle('');
    } catch (e) {
      if (e instanceof TypeError || /fetch|network|Failed to fetch|conexión/i.test(e.message || '')) {
        enqueue({ kind: 'justificarRuta', url, body: payload });
        setShowJustificarModal(false);
        setRutaAnteriorPendiente(null);
        setJustificarMotivo('');
        setJustificarDetalle('');
      } else {
        setError(e.message || 'Error al enviar la justificación');
      }
    } finally {
      setEnviandoJustificacion(false);
    }
  };

  const [rutaConfirmada, setRutaConfirmada] = useState(null); // { orders: [{id, orden, horaInicio, ...}] }
  const [routeMapOrders, setRouteMapOrders] = useState([]);
  const [loadingRoute, setLoadingRoute]     = useState(false);

  const googleMapsRouteUrl = useMemo(() => {
    const valid = routeMapOrders.filter(o => o.lat && o.lng);
    if (!valid.length) return null;
    const origin = `${OFICINA_COORDS.lat},${OFICINA_COORDS.lng}`;
    const last = valid[valid.length - 1];
    const destination = `${last.lat},${last.lng}`;
    const waypoints = valid.slice(0, -1).map(o => `${o.lat},${o.lng}`).join('|');
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (waypoints) url += `&waypoints=${encodeURIComponent(waypoints)}`;
    return url;
  }, [routeMapOrders]);

  const mapCenter = useMemo(() => {
    if (userLocation) return userLocation;
    const valid = routeMapOrders.filter(o => o.lat && o.lng);
    if (!valid.length) return OFICINA_COORDS;
    const lat = valid.reduce((s, o) => s + o.lat, 0) / valid.length;
    const lng = valid.reduce((s, o) => s + o.lng, 0) / valid.length;
    return { lat, lng };
  }, [routeMapOrders, userLocation]);

  // Cuando hay GPS del técnico, re-ordena por cercanía a él; si no, mantiene orden de oficina
  const sortedRouteOrders = useMemo(() => {
    if (userLocation) return ordenarPorDistanciaDesde(routeMapOrders, userLocation);
    return routeMapOrders;
  }, [routeMapOrders, userLocation]);

  const handleOpenRouteMap = async () => {
    setShowRouteMap(true);
    setLoadingRoute(true);
    try {
      const resp = await fetch(
        `/.netlify/functions/geocodifyTechnicianRoute?employeeId=${user.employeeId}&date=${today}`,
        { headers: { ...authService.authHeader() } }
      );
      const data = await resp.json();
      if (!data.success) throw new Error(data.error);
      const mapped = (data.orders || []).map(o => ({ ...o, direccion: o.addressText, tecnico: user?.name || 'Técnico' }));
      let sorted;
      if (rutaConfirmada?.orders?.length) {
        // Ruta confirmada disponible → respetar el orden planificado por el gestor
        const rutaMap = {};
        for (const r of rutaConfirmada.orders) rutaMap[String(r.id)] = r;
        sorted = [...mapped].sort((a, b) => {
          const ra = rutaMap[String(a.id)];
          const rb = rutaMap[String(b.id)];
          if (!ra && !rb) return 0;
          if (!ra) return 1;
          if (!rb) return -1;
          return (ra.orden || 99) - (rb.orden || 99);
        });
      } else {
        sorted = ordenarOrdenesPorDistancia(mapped);
      }
      setRouteMapOrders(sorted);
    } catch (e) {
      setError(e.message || 'Error cargando la ruta');
      setShowRouteMap(false);
    } finally {
      setLoadingRoute(false);
    }
  };

  if (selectedOrder) {
    return (
      <TechnicianOrderDetailView
        order={selectedOrder}
        onBack={(result) => {
          setSelectedOrder(null);
          if (!result) return;
          if (result.refresh) {
            // La visita ya se guardó de verdad en el servidor — recargar
            // desde ahí es seguro y trae cualquier otro cambio (p.ej. estado
            // automático aplicado por el propio backend).
            loadOrders(true);
          } else if (result.patch) {
            // Sin conexión: la visita quedó en la cola offline, todavía no
            // existe en el servidor. Recargar ahora mismo solo daría un error
            // de red y haría reaparecer la orden como si no se hubiera
            // tocado — se aplica el mismo cambio optimista sobre la lista ya
            // cargada en su lugar; el "Actualizar" manual (o el próximo
            // envío real de la cola) ya la pondrá al día de verdad.
            setOrders(prev => prev.map(o =>
              String(o.id) === String(result.patch.id) ? { ...o, ...result.patch } : o
            ));
          }
          // "Iniciar visita" vuelve por este mismo onBack (para que la lista
          // se refresque igual, ver TechnicianOrderDetailView.js) pero no es
          // un cierre de visita — no debe decir "Visita finalizada".
          if (result.kind !== 'iniciarVisita') {
            setSuccessToast('Visita finalizada correctamente');
            setTimeout(() => setSuccessToast(''), 3000);
          }
        }}
      />
    );
  }

  if (showRouteMap) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-white flex-shrink-0">
          <button
            onClick={() => setShowRouteMap(false)}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1">
            <h2 className="font-bold text-gray-900 text-base">Ruta del día</h2>
            <p className="text-xs text-gray-500">
              {sortedRouteOrders.length} paradas ·{' '}
              {userLocation ? 'ordenadas por cercanía a ti' : rutaConfirmada ? 'según ruta planificada' : 'de más lejos a más cerca'}
            </p>
          </div>
          {googleMapsRouteUrl && (
            <a
              href={googleMapsRouteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-11 h-11 bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-full flex items-center justify-center shadow-md transition-colors flex-shrink-0"
              title="Abrir ruta en Google Maps"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </a>
          )}
        </div>

        {/* Mapa */}
        <div className="flex-shrink-0" style={{ height: '52vh' }}>
          {loadingRoute ? (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
                <p className="text-gray-600 text-sm">Geocodificando direcciones...</p>
              </div>
            </div>
          ) : (
            <MapComponent
              center={mapCenter}
              markers={sortedRouteOrders}
              userLocation={userLocation}
              loading={false}
              showLegend={false}
            />
          )}
        </div>

        {/* Pill de GPS */}
        <div className="flex-shrink-0 px-4 py-2 flex items-center gap-2">
          {userLocation ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-3 py-1 font-medium">
              <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
              GPS activo · lista ordenada por cercanía a ti
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-full px-3 py-1">
              <span className="w-2 h-2 rounded-full bg-gray-300" />
              Esperando GPS…
            </span>
          )}
        </div>

        {/* Lista ordenada */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {loadingRoute ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
              <p className="text-sm">Geocodificando direcciones...</p>
            </div>
          ) : sortedRouteOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <span className="text-4xl mb-2">📍</span>
              <p className="text-sm">No hay órdenes con ubicación disponible</p>
            </div>
          ) : (
            sortedRouteOrders.map((order, idx) => {
              const distancia = userLocation && order.lat && order.lng
                ? calcularDistancia(userLocation.lat, userLocation.lng, order.lat, order.lng)
                : order.distanciaOficina;
              return (
                <div key={order.id} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow">
                    {idx + 1}
                  </div>
                  <div className="ph-mask flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">
                      {order.clientName || 'Sin nombre'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {order.addressText || 'Sin dirección'}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold text-blue-600">
                      {formatearDistancia(distancia)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {userLocation ? 'de ti' : 'de oficina'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">

      {/* ── Mejora 3: ruta anterior sin cerrar — pop-up que bloquea las
          órdenes de hoy hasta cerrarla. z-30 a propósito: por debajo de la
          cabecera y la barra inferior (z-40) de TechnicianApp, así el técnico
          puede seguir yendo a otras pestañas; solo quedan bloqueadas las
          órdenes. El cierre usa el mismo flujo que el de hoy
          (aplicarCambiosYCerrar) con la fecha de ese día. ── */}
      {!checkingRutaAnterior && rutaAnteriorPendiente && !showBlockingCloseModal && !showJustificarModal && (
        <div className="fixed inset-0 z-30 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center text-3xl">🚫</div>
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Cierra primero la ruta anterior</h3>
              <p className="text-sm text-gray-600 mt-2">
                La ruta del <strong>{new Date(rutaAnteriorPendiente.fecha + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</strong> está sin cerrar.
                Por favor, ciérrala para poder empezar con las órdenes de hoy.
              </p>
            </div>
            <button
              onClick={handleOpenBlockingCloseModal}
              disabled={loadingBlockingOrders}
              className="w-full py-3.5 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white font-bold rounded-2xl text-sm transition-colors shadow-lg"
            >
              {loadingBlockingOrders ? 'Cargando órdenes de ese día…' : 'Cerrar ruta del día anterior'}
            </button>
            <button
              onClick={() => setShowJustificarModal(true)}
              className="w-full text-xs text-gray-500 underline hover:text-gray-700"
            >
              No pude cerrarla — contar qué pasó
            </button>
          </div>
        </div>
      )}

      {/* Toast éxito tras finalizar visita */}
      {successToast && (
        <div className="fixed top-4 left-4 right-4 z-50 bg-emerald-600 text-white rounded-2xl px-4 py-3 flex items-center gap-2 shadow-lg">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          {successToast}
        </div>
      )}

      {/* Resumen del día */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Órdenes del día</h2>
            <p className="text-sm text-gray-500 capitalize">
              {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
          </div>
          <button
            onClick={() => loadOrders(true)}
            disabled={loading}
            title="Actualizar órdenes"
            className="flex flex-col items-center gap-0.5 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-40 flex-shrink-0 pt-0.5"
          >
            <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {lastUpdate && (
              <span className="text-xs leading-none">{formatAge(lastUpdate)}</span>
            )}
          </button>
        </div>
        <div className="flex gap-3 mt-3">
          <div className="flex-1 bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-gray-800">{assignedCount}</p>
            <p className="text-xs text-gray-500 mt-0.5">Asignadas</p>
          </div>
          <div className="flex-1 bg-green-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-700">{completedCount}</p>
            <p className="text-xs text-green-600 mt-0.5">Completadas</p>
          </div>
          <div className="flex-1 bg-blue-50 rounded-xl p-3 text-center">
            <p className={`text-2xl font-bold ${completionPct >= 100 ? 'text-green-600' : completionPct > 0 ? 'text-amber-600' : 'text-gray-500'}`}>
              {completionPct}%
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Completitud</p>
          </div>
        </div>
      </div>

      {/* Banner cierre exitoso */}
      {closedResult && (
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-2xl p-4 text-white">
          <p className="font-bold text-base">✓ Ruta del día cerrada</p>
          <div className="flex items-center gap-6 mt-2">
            <div className="text-center">
              <p className="text-2xl font-bold">{closedResult.completedCount}/{closedResult.assignedCount}</p>
              <p className="text-xs opacity-80">Firmadas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold">{closedResult.completionPct}%</p>
              <p className="text-xs opacity-80">Completitud</p>
            </div>
            {closedResult.efectivo > 0 && (
              <div className="text-center">
                <p className="text-2xl font-bold">{closedResult.efectivo.toFixed(2)}€</p>
                <p className="text-xs opacity-80">Efectivo</p>
              </div>
            )}
          </div>
          <button onClick={() => setClosedResult(null)} className="mt-3 text-xs opacity-70 underline">
            Cerrar aviso
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
          <span>⚠️</span>
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Botones de ruta — orders.length a propósito, no assignedCount: aquí
          debe verse en cuanto haya CUALQUIER orden asignada hoy (incluidas
          las "Pendiente" sin iniciar), no solo las accionables. */}
      {!loading && orders.length > 0 && (
        <div className="flex gap-2">
          <button
            onClick={handleOpenRouteMap}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold rounded-2xl transition-colors shadow text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
            </svg>
            Ver Ruta
          </button>
          {(closedResult || existingClosure) ? (
            <div className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-green-600 text-white font-bold rounded-2xl text-sm cursor-default shadow">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              Ruta Cerrada
            </div>
          ) : (
            <button
              onClick={handleOpenCloseModal}
              disabled={refreshingAssets}
              className="flex-1 py-3.5 bg-red-600 hover:bg-red-700 active:bg-red-800 disabled:opacity-60 text-white font-bold rounded-2xl transition-colors shadow-lg text-sm"
            >
              {refreshingAssets ? 'Actualizando órdenes...' : 'Cerrar Ruta del Día'}
            </button>
          )}
        </div>
      )}

      {/* Banner ruta confirmada */}
      {rutaConfirmada && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-sm text-blue-800">
          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
          </svg>
          <span className="font-semibold">Ruta planificada activa</span>
          <span className="text-blue-600">— órdenes ordenadas según la propuesta del día</span>
        </div>
      )}

      {/* Lista de órdenes — no se pinta mientras se comprueba la ruta
          anterior, para que no se pueda abrir una orden en ese segundo
          antes de que salga el pop-up de bloqueo. */}
      {(loading || checkingRutaAnterior) ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <div className="text-5xl mb-3">📋</div>
          <p className="font-semibold">Sin órdenes asignadas hoy</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(() => {
            // Si hay ruta confirmada, ordenar según el campo 'orden' del documento Firestore
            if (rutaConfirmada?.orders?.length) {
              const rutaMap = {};
              for (const r of rutaConfirmada.orders) {
                rutaMap[String(r.id)] = r;
              }
              const sorted = [...orders].sort((a, b) => {
                const ra = rutaMap[String(a.id)];
                const rb = rutaMap[String(b.id)];
                if (!ra && !rb) return 0;
                if (!ra) return 1;
                if (!rb) return -1;
                return (ra.orden || 99) - (rb.orden || 99);
              });
              return sorted.map(order => ({ order, rutaInfo: rutaMap[String(order.id)] || null }));
            }
            return orders.map(order => ({ order, rutaInfo: null }));
          })().map(({ order, rutaInfo }) => {
            const stateId = order['document-state-id'];
            const borderCls = order.signed || stateId === STATE_COLLECTED
              ? 'border-l-emerald-400'
              : stateId === STATE_ABSENT
              ? 'border-l-amber-400'
              : stateId === STATE_PENDING_PAYMENT
              ? 'border-l-red-400'
              : stateId === STATE_PENDING_MATERIAL
              ? 'border-l-orange-400'
              : stateId === 1040205
              ? 'border-l-purple-400'
              : stateId === STATE_PENDING_SUBSTITUTION
              ? 'border-l-cyan-400'
              : DONE_STATES.has(stateId)
              ? 'border-l-teal-400'
              : 'border-l-transparent';
            return (
              <button
                key={order.id}
                data-attr="orden-tarjeta"
                onClick={() => setSelectedOrder(order)}
                className={`w-full bg-white rounded-2xl p-4 shadow-sm text-left active:scale-[0.98] transition-transform border-l-4 border ${borderCls}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Badge nº de orden si hay ruta confirmada */}
                    {rutaInfo && (
                      <div className="flex-shrink-0 flex flex-col items-center gap-0.5 pt-0.5">
                        <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center leading-none">
                          {rutaInfo.orden}
                        </span>
                        {rutaInfo.horaInicio && (
                          <span className="text-[9px] font-mono text-blue-600 font-semibold leading-none mt-0.5">
                            {rutaInfo.horaInicio}
                          </span>
                        )}
                      </div>
                    )}
                    {/* ph-mask: nombre, dirección y notas del cliente tapados en la grabación de PostHog */}
                    <div className="ph-mask flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm truncate">
                        {order.clientName || 'Sin nombre'}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {order.addressText || 'Sin dirección'}
                      </p>
                      {order['private-comments'] && (
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{order['private-comments']}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATE_COLOR[stateId] || 'bg-gray-100 text-gray-600'}`}>
                      {STATE_LABEL[stateId] || '–'}
                    </span>
                    {order.signed && (
                      <span className="flex items-center gap-0.5 text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        Firmada
                      </span>
                    )}
                    {/* Antes había aquí un badge "✓ Procesada" (ámbar) para
                        cualquier estado cerrado sin firma — Rafa no entendía
                        qué significaba, y era redundante con el borde de color
                        de la tarjeta (ya distingue ausente/falta material/
                        presupuesto/sustitución/etc. cada uno con su color). Se
                        quita: si está de verdad terminada se ve en verde
                        ("Firmada"/borde esmeralda); si no, no hace falta nada
                        más (Rafa, 2026-10-15). */}
                  </div>
                </div>
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-400">
                  <span>{order['full-reference'] || `#${order.id}`}</span>
                  {order['work-order-type']?.name && (
                    <><span>·</span><span className="truncate">{order['work-order-type'].name}</span></>
                  )}
                  {rutaInfo?.duracion && (
                    <><span>·</span><span className="text-blue-500">{rutaInfo.duracion}min est.</span></>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Modal cierre de ruta */}
      {showCloseModal && validation && (
        <ModalCierreRuta
          validation={validation}
          totalOrdenes={assignedCount}
          titulo="Cerrar ruta del día"
          labelEfectivo="💶 Efectivo a entregar mañana"
          confirmando={closing}
          aplicandoEstados={applyingStates}
          onCancelar={() => setShowCloseModal(false)}
          onConfirmar={handleCloseRoute}
          onBuscarOrden={findOrderByRef}
          onSeleccionarOrden={(ordenRef) => { setShowCloseModal(false); setSelectedOrder(ordenRef); }}
          onElegirTipoIntervencion={(id, tipo) => elegirTipoIntervencion(id, tipo)}
          guardandoTipo={guardandoTipo}
          errorTipo={errorTipo}
        />
      )}

      {/* Modal cierre de la ruta ANTERIOR pendiente (Mejora 3) — mismo
          componente que el cierre de hoy (ModalCierreRuta), con su propio
          estado (blockingValidation/blockingOrders), sin tocar validation/
          orders de hoy. Antes era una copia de JSX aparte: no mostraba los
          cambios de estado automáticos ni dejaba saltar a la orden desde un
          error/aviso — ver handleConfirmBlockingClose para el porqué ya
          tampoco se desincroniza en la aplicación real de esos cambios. */}
      {showBlockingCloseModal && blockingValidation && (
        <ModalCierreRuta
          validation={blockingValidation}
          totalOrdenes={blockingOrders.length}
          titulo={`Cerrar ruta del ${new Date(rutaAnteriorPendiente.fecha + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'long' })}`}
          labelEfectivo="💶 Efectivo a entregar"
          confirmando={closingBlocking}
          aplicandoEstados={applyingStates}
          onCancelar={() => setShowBlockingCloseModal(false)}
          onConfirmar={handleConfirmBlockingClose}
          onBuscarOrden={(ref) => blockingOrders.find(o => (o['full-reference'] || `#${o.id}`) === ref) || null}
          onSeleccionarOrden={(ordenRef) => { setShowBlockingCloseModal(false); setSelectedOrder(ordenRef); }}
          onElegirTipoIntervencion={(id, tipo) => elegirTipoIntervencion(id, tipo, { anterior: true })}
          guardandoTipo={guardandoTipo}
          errorTipo={errorTipo}
        />
      )}

      {/* Modal justificar ruta anterior sin cerrar (Mejora 3) */}
      {showJustificarModal && rutaAnteriorPendiente && (
        <div className="fixed inset-0 z-50 flex items-end bg-black/50">
          <div className="bg-white w-full rounded-t-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto" />
            <h3 className="font-bold text-gray-900 text-lg">
              Justificar ruta del {new Date(rutaAnteriorPendiente.fecha + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'long' })}
            </h3>
            <p className="text-sm text-gray-500">
              Esto no cierra la ruta — solo queda anotado el motivo para que dirección lo vea.
            </p>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Motivo</label>
              <div className="grid grid-cols-2 gap-2">
                {MOTIVOS_JUSTIFICACION.map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setJustificarMotivo(v)}
                    className={`py-2 px-2 rounded-xl text-sm font-medium border transition-colors ${
                      justificarMotivo === v ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-300'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Qué pasó (obligatorio)</label>
              <textarea
                value={justificarDetalle}
                onChange={e => setJustificarDetalle(e.target.value)}
                rows={3}
                placeholder="Cuéntanos qué pasó ese día..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div className="flex gap-3 pt-1">
              <button
                onClick={() => setShowJustificarModal(false)}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 font-semibold rounded-xl transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleEnviarJustificacion}
                disabled={enviandoJustificacion || !justificarMotivo || !justificarDetalle.trim()}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-colors text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {enviandoJustificacion ? 'Enviando...' : 'Enviar justificación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

